import React, { useState, useEffect } from 'react';
import {
  Container, CssBaseline, Alert, Snackbar, Modal, Box, CircularProgress,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CategoryTabs from './components/CategoryTabs';
import ItemList from './components/ItemList';
import Cart from './components/Cart';
import theme from './theme';
import Layout from './Layout';
import Checkout from './components/Checkout';
import OrderSummaryReview from './components/OrderSummaryReview';
import OrderSummaryPrint from './components/OrderSummaryPrint';
import { useAppContext } from './AppContext';
import { saveOrder } from './orderService';


const NewOrder = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [currentStep, setCurrentStep] = useState('items');
  const { shopId, isOnShift, credit, userId, userName, saleTax, t } = useAppContext();
  const [isOffline, setIsOffline] = useState(false); 
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [showOrderSummaryPrint,setShowOrderSummaryPrint]=useState(false);
  const [orderDetails, setOrderDetails] = useState({
    cartItems: [],
    customerName: '',
    deliveryAddress: '',
    deliveryFeeAmount: 0,
    deliveryFeeType: null,
    discount: 0,
    fulfillmentDate: null,
    fulfillmentMethod: 'Pickup',
    fulfillmentTime: null,
    inscription: '',
    orderDate: new Date(),
    orderID: Date.now().toString().slice(-6),
    orderImages: [],
    orderImagePreviews: [],
    orderNotes: '',
    orderStatus: 'Unfulfilled',
    payments: [{ method: 'Cash', amount: 0 }], // Initialize with one payment
    phoneNumber: '',
    paymentAmount: 0,
    remainingBalance: 0,
    shopId: shopId,
    subtotal: 0,
    tax: 0,
    total: 0,
    userId: userId,
    userName: userName,
    cashierName:'',
  });

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  
  useEffect(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * (saleTax / 100);
    const total = subtotal + tax + orderDetails.deliveryFeeAmount - orderDetails.discount;
    const totalPaid = orderDetails.payments.reduce((sum, payment) => sum + payment.amount, 0);
    const remainingBalance = total - totalPaid;

    setOrderDetails(prev => ({
      ...prev,
      inscription: [...new Set(cart.map(item => item.inscription))].join(', '),
      cartItems: cart,
      subtotal,
      tax,
      total,
      paymentAmount: totalPaid,
      remainingBalance
    }));
  }, [cart, saleTax, orderDetails.deliveryFeeAmount, orderDetails.discount, orderDetails.payments]);


  const handleUpdateOrderDetails = (updates) => {

    setOrderDetails(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
  };

  const handleAddToCart = (item) => {
    setCart((prevCart) => [...prevCart, item]);
  };

  const handleProceedToCheckout = () => {
    if (credit > 0) {
      setCurrentStep('checkout');
    } else {
      setAlert({ open: true, message: t('Insufficient credit. Please add funds to proceed.'), severity: 'error' });
      setSnackbarOpen(true);
    }
  };

  const handleBackToItems = () => {
    setCurrentStep('items');
  };

  const handleMoveToReview = () => {
    setCurrentStep('review');
  };

  const handleBackToCheckout = () => {
    setCurrentStep('checkout');
  };

  const handleCancel = () => {
    setCart([]);
    setCurrentStep('items');
    setOrderDetails({
      cartItems: [],
      customerName: '',
      deliveryAddress: '',
      deliveryFeeAmount: 0,
      deliveryFeeType: null,
      discount: 0,
      fulfillmentDate: null,
      fulfillmentMethod: 'Pickup',
      fulfillmentTime: null,
      inscription: '',
      orderDate: new Date(),
      orderID: Date.now().toString().slice(-6),
      orderImages: [],
      orderImagePreviews: [],
      orderNotes: '',
      orderStatus: 'Unfulfilled',
      payments: [{ method: 'Cash', amount: 0 }],
      phoneNumber: '',
      remainingBalance: 0,
      shopId: shopId,
      subtotal: 0,
      tax: 0,
      total: 0,
      userId: userId,
      userName: userName,
      cashierName:'',
    });
  };

  const handleDeleteItem = (itemName) => {
    setCart((prevItems) => prevItems.filter((item) => item.name !== itemName));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCreateOrder = async () => {
    try {
      setIsLoading(true);
      await saveOrder(orderDetails, orderDetails.orderImages,shopId,userId,userName);
    } catch (error) {
      setAlert({ open: true, message: t('Error creating order'), severity: 'error' });
    } finally{
      setIsLoading(false); 
      setAlert({ open: true, message: t('Order created successfully, 1 credit deducted.'), severity: 'success' });
      setSnackbarOpen(true);
      setShowOrderSummaryPrint(true);
    }
  };

  const handleOrderSummaryPrintClose = () => {
    setCart([]);
    setCurrentStep('items');
    setOrderDetails({
      cartItems: [],
      customerName: '',
      deliveryAddress: '',
      deliveryFeeAmount: 0,
      deliveryFeeType: null,
      discount: 0,
      fulfillmentDate: null,
      fulfillmentMethod: 'Pickup',
      fulfillmentTime: null,
      inscription: '',
      orderDate: new Date(),
      orderID: Date.now().toString().slice(-6),
      orderImages: [],
      orderImagePreviews: [],
      orderNotes: '',
      orderStatus: 'Unfulfilled',
      payments: [{ method: 'Cash', amount: 0 }],
      phoneNumber: '',
      remainingBalance: 0,
      shopId: shopId,
      subtotal: 0,
      tax: 0,
      total: 0,
      userId: userId,
      userName: userName,
      cashierName:'',
    });
    setShowOrderSummaryPrint(false);
  };

  const LoadingModal = ({ isLoading }) => (
    <Modal open={isLoading}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1500, // Ensure loading modal appears above other modals
        }}
      >
        <CircularProgress />
      </Box>
    </Modal>
  );
  

  return (
    <ThemeProvider theme={theme}>
    <CssBaseline />
    <Layout>
    <Container sx={{m:0}}>
      {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t('You are offline. Some features may not be available.')}
            </Alert>
          )}

      {!isOnShift ? (
        <Alert severity="error" sx={{marginTop:'10px'}}>{t('You are not on shift. Please start your shift to proceed.')}</Alert>
      ) : (
          <>
             <Typography variant="h4" sx={{ mt: 3, mb: 1 }}>{t('New Order')}</Typography>
              {currentStep === 'items' && (
                <>
                  <CategoryTabs
                    selectedCategory={selectedCategory}
                    onSelectCategory={handleSelectCategory}
                    shopId={shopId}
                  />
                  <ItemList
                    category={selectedCategory}
                    onAddToCart={handleAddToCart}
                    shopId={shopId}
                    productType='Order'
                  />
                  {cart.length > 0 && (
                    <Cart
                      cartItems={cart}
                      onProceedToCheckout={handleProceedToCheckout}
                      onCancel={handleCancel}
                      onDeleteItem={handleDeleteItem}
                    />
                  )}
                </>
              )}

              {currentStep === 'checkout' && (
                <Checkout
                  cartItems={cart}
                  onBackToShopping={handleBackToItems}
                  onCancelOrder={handleCancel}
                  onMoveToReview={handleMoveToReview}
                  orderDetails={orderDetails}
                  onUpdateOrderDetails={handleUpdateOrderDetails}
                />
              )}

              {currentStep === 'review' && (
                <OrderSummaryReview
                  orderDetails={orderDetails}
                  onEdit={handleBackToCheckout}
                  onConfirm={handleCreateOrder}
                  onCancel={handleCancel}
                  cartItems={cart}
                />
              )}
            </>
          )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
        {alert.message}
      </Alert>
      </Snackbar>
      <LoadingModal isLoading={isLoading} />
   </Container>
   {showOrderSummaryPrint && (
          <Modal open={true}>
            <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
            }}>
            <OrderSummaryPrint
                  orderDetails={orderDetails}
                  onClose={handleOrderSummaryPrintClose}
                />
                </Box>
                </Modal>
          )}
   </Layout>
  </ThemeProvider>
  

  );
};

export default NewOrder;